// /en/police-clearance/$type
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { POLICE_CLEARANCE_TYPES, getPoliceType } from "@/data/police-clearance-types";
import { policeTypeSectionsEn } from "@/data/police-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { ShieldCheck, MessageCircle, Phone, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/police-clearance/$type")({
  loader: ({ params }) => {
    if (!getPoliceType(params.type)) throw notFound();
    return { slug: params.type };
  },
  head: ({ params }) => {
    const t = getPoliceType(params.type);
    if (!t) return { meta: [{ title: ctaTitle("Not found", "/en/police-clearance/$type", "en")}] };
    const url = `${SITE}/en/police-clearance/${t.slug}`;
    const title = `${t.nameEn} from THB ${t.priceFromTh.toLocaleString()}, ${t.turnaroundEn} | NYC Police Clearance`;
    const desc = `${t.oneLineEn} · Issued by ${t.agencyEn} · ${t.legalizationPath} — Free consultation, fast reply.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/police-clearance/$type", "en") },
        { name: "description", content: ctaDesc(desc, "/en/police-clearance/$type", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/police-clearance/$type", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/police-clearance/$type", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/police-clearance/${t.slug}`, `/en/police-clearance/${t.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: t.nameEn,
              serviceType: "Police Clearance",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: t.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: PoliceTypeEnPage,
});

function PoliceTypeEnPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const t = getPoliceType(slug)!;
  const sections = policeTypeSectionsEn(t);
  const related = POLICE_CLEARANCE_TYPES.filter((x) => x.slug !== slug).slice(0, 6);
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance" className="hover:text-gold">Police Clearance</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{t.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> {t.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">{t.nameEn}</h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{t.oneLineEn}</p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {t.oneLineEn}
        </blockquote>

        <div className="mt-10"><PoliceSections sections={sections} /></div>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `How much is ${t.nameEn}?`, a: `From THB ${t.priceFromTh.toLocaleString()}, completed in ${t.turnaroundEn}.` },
                { q: `Which countries accept this?`, a: t.acceptedBy.join(", ") },
                { q: `What documents are needed?`, a: t.required.join(" · ") },
                { q: `Legalization process?`, a: t.legalizationPath },
              ]}
            />
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Other Services</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/en/police-clearance/$type" params={{ type: r.slug }} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50">
                  <div className="font-display text-sm font-extrabold text-navy-deep">{r.nameEn}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">From THB {r.priceFromTh.toLocaleString()} · {r.turnaroundEn}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="police" locale="en" variant="light" />
    </main>
  );
}
